import { isEmpty } from 'radash';

import { JobDataType, JobEventDataType } from './executed.publication.types';

export type DestinationSource = {
    id?: number;
    name?: string;
    organizationId?: number;
    needsVerification?: boolean;
};

export const isScheduledJobBusy = (job: Pick<JobDataType, 'status'>): boolean =>
    job.status === 'BUSY';

export const isScheduledJobAllowed = (
    job: Pick<JobDataType, 'user' | 'destinationChannelIds'>,
    isVerificationRequired: boolean,
    user: { roles?: string[]; userId?: string | number } | undefined,
    organization: { verified?: unknown } | undefined,
    channels: readonly DestinationSource[] | undefined,
): boolean => {
    const userAllowed =
        Boolean(user?.roles?.includes('admin')) ||
        isEmpty(job.user) ||
        job.user === (user?.userId ?? null);

    if (!userAllowed) {
        return false;
    }

    if (!isVerificationRequired) {
        return true;
    }

    if (organization?.verified) {
        return true;
    }

    const destinationChannelIds = job.destinationChannelIds ?? [];
    if (destinationChannelIds.length === 0) {
        return false;
    }

    return !destinationChannelIds.some((channelId) =>
        channels?.some((channel) => channel.id === channelId && channel.needsVerification),
    );
};

export const resolveJobDestinations = (
    job: Pick<
        JobDataType,
        | 'channelSubDestinations'
        | 'destinationChannelIds'
        | 'destinationId'
        | 'destinationOrganizationIds'
        | 'subDestination'
    >,
    sources: readonly DestinationSource[],
): string => {
    const destinations: string[] = [];

    if (job.subDestination) {
        destinations.push(job.subDestination);
        return destinations.join(', ');
    }

    for (const subDestination of job.channelSubDestinations ?? []) {
        const name = subDestination.split(':')[1];
        if (name) {
            destinations.push(name);
        }
    }

    const ids = [
        ...(job.destinationChannelIds ?? []),
        ...(job.destinationOrganizationIds ?? []),
        ...(job.destinationId != null ? [job.destinationId] : []),
    ];

    for (const id of ids) {
        const element = sources.find(
            (source) => source.id == id || source.organizationId == id,
        );
        if (element?.name) {
            destinations.push(element.name);
        }
    }

    return destinations.join(', ');
};

export const enrichScheduledJob = (
    job: JobDataType,
    sources: readonly DestinationSource[],
): JobDataType => ({
    ...job,
    destinationName: resolveJobDestinations(job, sources),
});

/**
 * Angular `PublicationOverviewController` only patches an existing row when
 * `publicationJobChanged` arrives with `typeOfChange === 'updated'`.
 */
export const mergeScheduledJobUpdate = (
    jobs: JobDataType[],
    event: JobEventDataType | undefined,
): JobDataType[] => {
    if (event?.typeOfChange !== 'updated' || !event.job) {
        return jobs;
    }

    const incoming = {
        ...event.job,
        message: event.job.message || '',
    };

    return jobs.map((job) => {
        if (job.id != incoming.id) {
            return job;
        }

        return {
            ...job,
            ...incoming,
            schedule: {
                ...job.schedule,
                ...incoming.schedule,
            },
        };
    });
};
